'use client';

import { useEffect } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { RotateCw } from 'lucide-react';
import { NeuralPlayground } from '@/components/ui/neural-playground';

/**
 * Ошибка на домене DataHunter. Лежит внутри ветки `datahunter`, поэтому
 * наследует её тему, шапку и подвал — см. `not-found.tsx` рядом и
 * `aisearch/error.tsx`, откуда взят приём с `useParams()` вместо `params`
 * пропа: Error Boundary его не получает.
 */
export default function DataHunterError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const params = useParams<{ locale?: string }>();
  const t = useTranslations('dh_errors');

  useEffect(() => {
    console.error('Ошибка страницы DataHunter:', error.digest ?? error.message, params.locale);
  }, [error, params.locale]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-20 text-center relative overflow-hidden">
      <div className="relative w-full max-w-3xl h-[320px] sm:h-[380px] mb-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
        <NeuralPlayground className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="text-7xl sm:text-8xl font-bold text-[var(--color-text)]/90 leading-none">
            500
          </p>
        </div>
      </div>

      <p className="text-xs text-[var(--color-text-muted)] mb-8">{t('hint')}</p>

      <h1 className="text-2xl font-bold mb-3">{t('error_title')}</h1>
      <p className="text-[var(--color-text-muted)] mb-8 max-w-md leading-relaxed">
        {t('error_desc')}
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={reset}
          className="dh-cta inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl font-semibold transition-colors"
        >
          <RotateCw className="w-4 h-4" />
          {t('retry')}
        </button>
        <Link
          href="/"
          className="px-7 py-3 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-accent-500)] bg-[var(--color-bg-card)] font-semibold transition-colors"
        >
          {t('home')}
        </Link>
      </div>

      {error.digest && (
        <p className="mt-10 text-xs text-[var(--color-text-muted)]/70">
          ID: <code className="font-mono">{error.digest}</code>
        </p>
      )}
    </div>
  );
}
